import { type ComponentType, type ReactNode } from 'react';
import { LuSparkle } from 'react-icons/lu';
import { RiEditLine, RiFileCheckLine, RiStethoscopeLine } from 'react-icons/ri';
import type { AgentType } from '@/model/Agent.model';
import type { FeatureFlagType } from '@/model/FeatureFlag.model';

type IconComponent = ComponentType<{ size: number; className: string }>;

const agentIconMap: Record<AgentType, IconComponent> = {
  study_health_check: RiStethoscopeLine,
  study_protocol_parser: RiEditLine,
  send_validator: RiFileCheckLine,
};

/**
 * Maps each agent type to its kebab-case URL segment. The API exposes agent
 * slugs as snake_case enum values, but web routes use kebab-case, so this map
 * is the single source of truth for translating between the two.
 */
export const agentUrlSlugMap: Record<AgentType, string> = {
  study_health_check: 'study-health-check',
  study_protocol_parser: 'study-protocol-parser',
  send_validator: 'send-validator',
};

const urlSlugToAgentType = new Map<string, AgentType>(
  (Object.entries(agentUrlSlugMap) as Array<[AgentType, string]>).map(([type, urlSlug]) => [urlSlug, type])
);

/** Resolves a kebab-case URL segment back to its agent type, or undefined if unknown. */
export const parseAgentUrlSlug = (urlSlug: string): AgentType | undefined => urlSlugToAgentType.get(urlSlug);

/** Per-agent display strings for the session view: the friendly `name` and the in-progress `title`. */
interface AgentDisplay {
  /** Human-friendly agent name (shown in the session breadcrumb). */
  name: string;
  /** What the agent is doing, shown as the root node's title. */
  title: string;
}

/**
 * Display strings per agent type, used where only the agent slug is available (e.g. the session page,
 * which gets `agent_type` from the URL but no display copy). The `title` is per-agent because each
 * agent does something different.
 */
export const agentDisplayMap: Record<AgentType, AgentDisplay> = {
  study_health_check: { name: 'Study Health Check', title: 'Generating Report' },
  study_protocol_parser: { name: 'Study Protocol Parser', title: 'Parsing Protocol' },
  send_validator: { name: 'SEND Validator', title: 'Validating Domain' },
};

/**
 * Browser tab titles per agent. Kept separate from `agentDisplayMap` because the API still serves
 * the legacy `Study Protocol Parser` name that the hub cards and breadcrumbs render, so the rename
 * to `Study design agent` is applied at the title layer only.
 */
export const agentPageTitleMap: Record<AgentType, string> = {
  study_health_check: 'Study health check',
  study_protocol_parser: 'Study design agent',
  send_validator: 'SEND Validator',
};

/**
 * Returns the icon element associated with an agent slug.
 * Falls back to a sparkle icon for unrecognized slugs.
 */
export const getAgentIcon = (slug: AgentType): ReactNode => {
  const Icon = agentIconMap[slug] ?? LuSparkle;
  return <Icon size={26} className="blue" />;
};

/**
 * Maps each agent type to the feature flag that gates it. Agent slugs and flag names do
 * not always match (e.g. `study_protocol_parser` is gated by `study_design_agent`), so
 * this map is the single source of truth for that relationship. The `Record<AgentType, …>`
 * type ensures every agent declares a gating flag.
 */
const agentFlagMap: Record<AgentType, FeatureFlagType> = {
  study_health_check: 'study_health_check',
  study_protocol_parser: 'study_design_agent',
  send_validator: 'send_validator',
};

/**
 * Determines whether an agent should be visible/accessible. An agent is enabled only when
 * the master `ai_agents` flag is on AND the agent's own feature flag is on. Used to gate
 * the hub cards and the agent setup/session routes.
 */
export const isAgentEnabled = (features: Partial<Record<FeatureFlagType, boolean>>, slug: AgentType): boolean =>
  features.ai_agents === true && features[agentFlagMap[slug]] === true;
